import { useCallback, useState } from 'react';
import type { MusicVideoEditorPlan } from 'fym-shared/src/music24/musicVideoApi';
import { useVideoCreditGate } from '../../music24/promote/credits/useVideoCreditGate';
import { makeVideoError, type MakeVideoError } from '../doc/musicVideoErrors';
import { isCutSaved, type CutSaveState } from '../doc/useCutAutosave';
import { useMakeMusicVideoMutation } from '../musicVideoEditorApi';

/** Which dialog of "Make the video" is open: the confirm (e2) or the plan gate (e4). */
export type MakeDialog = 'none' | 'confirm' | 'pro';

interface UseMakeVideoProps {
  accountId: number;
  catalogItemUuid: string;
  cutUuid: string;
  plan: MusicVideoEditorPlan;
  /** Saves the open cut first: the server builds the video from the stored document. */
  flush: () => Promise<CutSaveState>;
}

/**
 * "Make the video" (design e2 to e5, spec §4.4 make). Below Pro the click opens the plan gate (e4);
 * with too few credits it opens the credit packs (e5) and sends nothing; else it asks (e2), saves
 * the cut and calls `POST .../make`. The answer is the queued kit asset (e3 follows it). A cut
 * that does not save (the flush answers another state than `saved`) is not made: the server would
 * render an older document. The dialog shows the save state (`saveError`) and nothing is charged.
 */
export function useMakeVideo({ accountId, catalogItemUuid, cutUuid, plan, flush }: UseMakeVideoProps) {
  const gate = useVideoCreditGate(accountId);
  const [makeMusicVideo, { isLoading }] = useMakeMusicVideoMutation();
  const [dialog, setDialog] = useState<MakeDialog>('none');
  const [error, setError] = useState<MakeVideoError | null>(null);
  const [saveError, setSaveError] = useState<CutSaveState | null>(null);
  const [makingAssetUuid, setMakingAssetUuid] = useState<string | null>(null);

  const start = useCallback(() => {
    setError(null);
    setSaveError(null);
    if (!plan.canRender) {
      setDialog('pro');
      return;
    }
    if (gate.isShort(plan.renderCredits)) {
      gate.offerCredits();
      return;
    }
    setDialog('confirm');
  }, [gate, plan.canRender, plan.renderCredits]);

  const confirm = useCallback(async (): Promise<void> => {
    setError(null);
    setSaveError(null);
    try {
      const saveState = await flush();
      if (!isCutSaved(saveState)) {
        setSaveError(saveState);
        return;
      }
      const { asset } = await makeMusicVideo({ accountId, catalogItemUuid, cutUuid }).unwrap();
      setMakingAssetUuid(asset.uuid);
      setDialog('none');
    } catch (caught: unknown) {
      const kind = makeVideoError(caught);
      console.error('Music video editor: the video did not start', caught);
      if (kind === 'plan') {
        setDialog('pro');
        return;
      }
      if (kind === 'credits') {
        setDialog('none');
        gate.offerCredits();
        return;
      }
      setError(kind);
    }
  }, [accountId, catalogItemUuid, cutUuid, flush, gate, makeMusicVideo]);

  const close = useCallback(() => {
    setDialog('none');
    setError(null);
    setSaveError(null);
  }, []);

  return {
    dialog,
    start,
    confirm,
    close,
    isBusy: isLoading,
    error,
    saveError,
    makingAssetUuid,
    clearMaking: () => setMakingAssetUuid(null),
  };
}
